import { useState } from "react";
import { Button, Stack } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { isFreeStack } from "@/helpers/tenant";
import { User } from "@/types";

export interface FilterValue {
  [key: string]: string | undefined;
}

export interface FilterOption {
  label: string;
  values: FilterValue[];
}

interface PreSelectFiltersProps {
  currentUser: User | null;
}

export const PreSelectFilters = ({ currentUser }: PreSelectFiltersProps) => {
  const { searchParams, clearSearchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const [showAll, setShowAll] = useState(false);

  const PRE_SELECT_FILTER_OPTIONS: FilterOption[] = [
    {
      label: "Assigned to me",
      values: [{ owner: currentUser?.id !== undefined ? String(currentUser.id) : undefined }],
    },
    {
      label: "Read Dormant Tables",
      values: [{ opportunity_name: "read_dormant_90d" }],
    },
    {
      label: "Zero impact queries",
      values: [{ opportunity_name: "zero_impact" }],
    },
    // "Implemented Opportunities" is now a /discover tab backed by its
    // own table — no longer a status quick-filter.
    {
      label: "Copy queries on big warehouse",
      values: [{ opportunity_name: "copy_on_big_warehouse" }],
    },
    {
      label: "Queries with remote spillage",
      values: [{ opportunity_name: "query_with_remote_spillage" }],
    },
    {
      label: "Read Dormant Tables (60 Days)",
      values: [{ opportunity_name: "read_dormant_60d" }],
    },
    {
      label: "Read Dormant Tables (30 Days)",
      values: [{ opportunity_name: "read_dormant_30d" }],
    },
  ];

  const PRE_SELECT_FILTER_OPTIONS_FREEMIUM: FilterOption[] = [
    {
      label: "Assigned to me",
      values: [{ owner: currentUser?.id !== undefined ? String(currentUser.id) : undefined }],
    },
    // "Implemented Opportunities" lives on a separate /discover tab now.
  ];

  const handlePreSelect = (values: FilterValue[]) => {
    // Preserve the active tab across the clear+re-apply so we don't
    // accidentally fall back to the default tab (e.g. Priority for
    // megatenant) while the user is filtering Opportunities.
    const activeTab = searchParams.get("tab");
    clearSearchParams();
    const next = values.reduce<FilterValue>(
      (acc, value) => ({ ...acc, ...value }),
      activeTab ? { tab: activeTab } : {}
    );
    updateSearchParamsByKey(next);
  };

  const isSelected = (values: FilterValue[]) => {
    const valuesMap = new Map<string, string[]>();
    values.forEach(value => {
      const [key, val] = Object.entries(value)[0];
      if (!valuesMap.has(key)) {
        valuesMap.set(key, []);
      }
      valuesMap.get(key)!.push(String(val));
    });

    for (const [key, valArray] of valuesMap.entries()) {
      const searchParamsValues = searchParams.getAll(key);
      if (searchParamsValues.length !== valArray.length) {
        return false;
      }
      if (!searchParamsValues.every(val => valArray.includes(val))) {
        return false;
      }
    }
    const totalUniqueKeysInSearchParams = new Set(Array.from(searchParams.keys())).size;
    return totalUniqueKeysInSearchParams === valuesMap.size;
  };

  const visibleOptions = showAll
    ? PRE_SELECT_FILTER_OPTIONS
    : PRE_SELECT_FILTER_OPTIONS.slice(0, 3);

  if (isFreeStack()) {
    return (
      <Stack direction="row" gap="sm" className="flex-wrap">
        {PRE_SELECT_FILTER_OPTIONS_FREEMIUM.map((option, i) => (
          <Button
            className={"al-font-normal"}
            key={i}
            variant={isSelected(option.values) ? "default" : "secondary"}
            onClick={() => handlePreSelect(option.values)}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap="sm" className="flex-wrap">
      {visibleOptions.map((option, i) => (
        <Button
          className={"al-font-normal"}
          key={i}
          variant={isSelected(option.values) ? "default" : "secondary"}
          onClick={() => handlePreSelect(option.values)}
        >
          {option.label}
        </Button>
      ))}
      {PRE_SELECT_FILTER_OPTIONS.length > 3 && (
        <span
          className="text-primary cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => setShowAll(!showAll)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") setShowAll(!showAll);
          }}
          style={{ marginLeft: "8px", alignSelf: "center" }}
        >
          {showAll ? "Less" : "More"}
        </span>
      )}
    </Stack>
  );
};
